import type {
  HelpAgentAttachmentKind,
  HelpAgentAttachmentPolicy,
} from '@/types/attachments';

const IMAGE_FILE_EXTENSIONS = new Set([
  '.apng',
  '.avif',
  '.bmp',
  '.gif',
  '.heic',
  '.heif',
  '.jpeg',
  '.jpg',
  '.png',
  '.svg',
  '.webp',
]);

export const DEFAULT_ATTACHMENT_POLICY: HelpAgentAttachmentPolicy = Object.freeze({
  maxFiles: 0,
  maxFileSizeBytes: 25 * 1024 * 1024,
  allowedMimeTypes: '*',
  allowedExtensions: '*',
});

export function normalizeAttachmentPolicy(
  policy: Partial<HelpAgentAttachmentPolicy> | null | undefined,
): HelpAgentAttachmentPolicy {
  const nextPolicy = {
    ...DEFAULT_ATTACHMENT_POLICY,
    ...(policy ?? {}),
  };

  return {
    ...nextPolicy,
    maxFiles: Math.max(0, Math.floor(nextPolicy.maxFiles)),
    maxFileSizeBytes: Math.max(1, Math.floor(nextPolicy.maxFileSizeBytes)),
  };
}

export function buildAttachmentAccept(policy: HelpAgentAttachmentPolicy): string {
  const acceptedParts = [
    ...(policy.allowedMimeTypes === '*' ? [] : policy.allowedMimeTypes),
    ...(policy.allowedExtensions === '*' ? [] : policy.allowedExtensions),
  ].map((value) => value.trim()).filter(Boolean);

  return acceptedParts.join(',');
}

function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
    return '';
  }

  return fileName.slice(lastDotIndex).toLowerCase();
}

function matchesAllowedMimeType(fileType: string, allowedMimeTypes: readonly string[] | '*'): boolean {
  if (allowedMimeTypes === '*') {
    return true;
  }

  if (!fileType) {
    return false;
  }

  const normalizedFileType = fileType.toLowerCase();
  return allowedMimeTypes.some((allowedType) => {
    const normalizedAllowedType = allowedType.toLowerCase();
    if (normalizedAllowedType.endsWith('/*')) {
      const prefix = normalizedAllowedType.slice(0, -1);
      return normalizedFileType.startsWith(prefix);
    }

    return normalizedAllowedType === normalizedFileType;
  });
}

function matchesAllowedExtension(fileName: string, allowedExtensions: readonly string[] | '*'): boolean {
  if (allowedExtensions === '*') {
    return true;
  }

  const extension = getFileExtension(fileName);
  if (!extension) {
    return false;
  }

  return allowedExtensions.some((allowedExtension) => {
    const normalizedAllowedExtension = allowedExtension.startsWith('.')
      ? allowedExtension.toLowerCase()
      : `.${allowedExtension.toLowerCase()}`;

    return normalizedAllowedExtension === extension;
  });
}

export function validateAttachmentFile(
  file: File,
  currentCount: number,
  policy: HelpAgentAttachmentPolicy,
): string | null {
  if (policy.maxFiles <= 0) {
    return 'Attachments are disabled.';
  }

  if (currentCount >= policy.maxFiles) {
    return `You can attach up to ${policy.maxFiles} files.`;
  }

  if (file.size > policy.maxFileSizeBytes) {
    const maxMb = Math.round(policy.maxFileSizeBytes / (1024 * 1024));
    return `Files must be ${maxMb} MB or smaller.`;
  }

  const mimeMatches = policy.allowedMimeTypes === '*'
    ? null
    : matchesAllowedMimeType(file.type, policy.allowedMimeTypes);
  const extensionMatches = policy.allowedExtensions === '*'
    ? null
    : matchesAllowedExtension(file.name, policy.allowedExtensions);

  const isAllowed = mimeMatches === null && extensionMatches === null
    ? true
    : mimeMatches === null
      ? Boolean(extensionMatches)
      : extensionMatches === null
        ? Boolean(mimeMatches)
        : mimeMatches || extensionMatches;

  if (!isAllowed) {
    return 'This file type is not allowed.';
  }

  return null;
}

export function inferAttachmentKind(file: Pick<File, 'name' | 'type'>): HelpAgentAttachmentKind {
  if (file.type.toLowerCase().startsWith('image/')) {
    return 'image';
  }

  return IMAGE_FILE_EXTENSIONS.has(getFileExtension(file.name)) ? 'image' : 'file';
}
